import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ModalShell from "./ModalShell";
import { Collection } from "../../../api/derin";
import { collectionColors, font, subtitleSx } from "./tokens";

interface DeleteCollectionDialogProps {
  open: boolean;
  collection: Collection | null;
  /** Whether any variant of the collection has collected responses. */
  hasResponses: boolean;
  /** Whether the collection is still live (its survey is collecting). */
  isActive?: boolean;
  /**
   * The dialog can open from a closed card before the full detail (variant rows) has
   * resolved. While true, the body shows a spinner and confirm is disabled, so no one
   * deletes before the responses warning is known.
   */
  loadingDetail?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
}

/**
 * Confirmation for deleting a whole collection (86bc66gkv).
 *
 * A delete is a soft delete, but it removes the collection and every survey and response
 * under it from the client app — far more than a mis-click should do. The dialog states
 * what is affected (name, how many variants, and a warning when any variant already has
 * responses) and only proceeds on explicit confirmation.
 */
const DeleteCollectionDialog: React.FC<DeleteCollectionDialogProps> = ({
  open,
  collection,
  hasResponses,
  isActive = false,
  loadingDetail = false,
  onClose,
  onConfirm,
  isSubmitting = false,
}) => {
  const variantCount = collection?.variantCount ?? 0;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Delete Collection"
      subtitle="This action cannot be undone."
      confirmLabel="Delete Collection"
      onConfirm={onConfirm}
      confirmDisabled={loadingDetail}
      isSubmitting={isSubmitting}
      danger
    >
      {collection && (
        <>
          {/* Collection being deleted — name + variant count */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "16px",
              background: collectionColors.surfaceLight,
              border: `1px solid ${collectionColors.border}`,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{
                fontFamily: font,
                fontWeight: 500,
                fontSize: "11px",
                lineHeight: "16px",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                color: collectionColors.textSecondary,
              }}
            >
              Collection to be deleted
            </Typography>
            <Typography
              sx={{
                fontFamily: font,
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "22px",
                color: collectionColors.textPrimary,
              }}
            >
              {collection.name}
            </Typography>
            {loadingDetail ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CircularProgress size={14} sx={{ color: collectionColors.textSecondary }} />
                <Typography sx={{ ...subtitleSx, fontSize: "13px", lineHeight: "18px" }}>
                  Loading collection details…
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <FolderOutlinedIcon
                  sx={{ width: 15, height: 15, color: collectionColors.textSecondary }}
                />
                <Typography sx={{ ...subtitleSx, fontSize: "13px", lineHeight: "18px" }}>
                  {variantCount} variant{variantCount === 1 ? "" : "s"} will be marked as deleted
                </Typography>
              </Box>
            )}
          </Box>

          {/* Active-collection warning — a live study would vanish from the client app mid-run */}
          {isActive && (
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                padding: "14px 16px",
                background: collectionColors.dangerSurface,
                border: `1px solid ${collectionColors.danger}`,
                borderRadius: "8px",
              }}
            >
              <ErrorOutlineIcon
                sx={{ width: 18, height: 18, color: collectionColors.danger, flexShrink: 0, mt: "1px" }}
              />
              <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: collectionColors.danger,
                  }}
                >
                  This collection is still active
                </Typography>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontWeight: 400,
                    fontSize: "13px",
                    lineHeight: "18px",
                    color: collectionColors.danger,
                  }}
                >
                  It is still collecting responses. Deleting it now removes it from the
                  client app immediately, even for participants mid-survey.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Responses warning — only when a variant has collected responses */}
          {hasResponses && (
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                padding: "14px 16px",
                background: collectionColors.dangerSurface,
                border: `1px solid ${collectionColors.danger}`,
                borderRadius: "8px",
              }}
            >
              <ErrorOutlineIcon
                sx={{ width: 18, height: 18, color: collectionColors.danger, flexShrink: 0, mt: "1px" }}
              />
              <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: collectionColors.danger,
                  }}
                >
                  This collection has collected responses
                </Typography>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontWeight: 400,
                    fontSize: "13px",
                    lineHeight: "18px",
                    color: collectionColors.danger,
                  }}
                >
                  All participant survey records linked to this collection will also be
                  marked as deleted and will no longer appear in the client app.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Scope explanation */}
          <Typography sx={{ ...subtitleSx, fontSize: "13px", lineHeight: "19px" }}>
            The collection and all its linked surveys will be removed from the client app
            and will no longer appear anywhere for participants.
          </Typography>
        </>
      )}
    </ModalShell>
  );
};

export default DeleteCollectionDialog;
